import { useState, useCallback, useMemo, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useDropzone } from "react-dropzone";
import { useTranslate } from "@tolgee/react";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CloseIcon from "@mui/icons-material/Close";
import usePresentationTranslation from "../hooks/usePresentationTranslation";
import useToolProject from "../hooks/useToolProject";
import { useAuthService } from "../Context/AuthContext";
import { useNotify } from "../Helpers/Notifications";
import { PRODUCT_SLUG_PRESENTATIONS, PLATFORM_URL } from "../Constants/constantValues";
import LocalStorage from "../Helpers/LocalStorage";
import LanguageBtn from "../Components/Auth Components/LanguageBtn";
import ProjectSelector from "../Components/GenericComponents/ProjectSelector";


const PresentationIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 3h20v14H2V3zm10 14v4m-4 0h8M8 10l3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const MAX_LANGUAGES = 5;

const ACCEPT = {
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
};

const getJobPercent = (job) => {
  if (job.status === "uploading") return 5;
  if (job.status === "completed") return 100;
  if (job.status === "failed") return 0;
  const { translated, total } = job.progress || {};
  if (!total || total === 0) return 8;
  return Math.round(10 + (translated / total) * 85);
};

const getJobPhaseText = (job, t) => {
  if (job.status === "pending") return t("presentation_tool.waiting");
  if (job.status === "uploading") return t("presentation_tool.uploading");
  if (job.status === "completed") return t("presentation_tool.complete");
  if (job.status === "failed") {
    if (job.error === "quota_exceeded") return t("presentation_tool.error_quota_exceeded");
    return t("presentation_tool.error_default");
  }
  const { extracted, translated, total } = job.progress || {};
  if (translated === total && total > 0) return t("presentation_tool.building");
  if (extracted > 0 && translated < total) {
    return t("presentation_tool.translating", { translated, total });
  }
  return t("presentation_tool.extracting");
};

const PresentationTranslationToolPage = () => {
  const [file, setFile] = useState(null);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const { t } = useTranslate();
  const { translationsLanguages, getTranslationsLanguages, getProductQuota, platformPresentations, authHasCompleted, checkSubscriptions } = useAuthService();
  const { projectId, projects, setSelectedId, isMultiple } = useToolProject(PRODUCT_SLUG_PRESENTATIONS);

  useEffect(() => {
    if (translationsLanguages.length !== 0) return;
    getTranslationsLanguages(
      function () {},
      function () {}
    );
  }, [translationsLanguages.length, getTranslationsLanguages]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") checkSubscriptions();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [checkSubscriptions]);
  const { notifyError } = useNotify();

  const { jobs, startTranslation, reset, downloadAll } =
    usePresentationTranslation(projectId);

  const hasJobs = jobs.length > 0;
  const allDone = hasJobs && jobs.every((j) => j.status === "completed" || j.status === "failed");
  const completedCount = jobs.filter((j) => j.status === "completed").length;

  const onDrop = useCallback(
    (accepted, rejected) => {
      if (rejected.length > 0) {
        notifyError(t("presentation_tool.invalid_file"));
        return;
      }
      if (accepted.length > 0) {
        setFile(accepted[0]);
      }
    },
    [notifyError, t]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPT,
    maxFiles: 1,
    disabled: hasJobs,
  });

  const availableLanguages = useMemo(
    () => translationsLanguages.filter((lang) => !selectedLanguages.includes(lang.name)),
    [translationsLanguages, selectedLanguages]
  );

  const addLanguage = useCallback(
    (val) => {
      if (!val || selectedLanguages.length >= MAX_LANGUAGES) return;
      setSelectedLanguages((prev) => [...prev, val]);
    },
    [selectedLanguages]
  );

  const removeLanguage = useCallback((lang) => {
    setSelectedLanguages((prev) => prev.filter((l) => l !== lang));
  }, []);

  const handleTranslate = useCallback(() => {
    if (!file || selectedLanguages.length === 0) return;

    getProductQuota(
      PRODUCT_SLUG_PRESENTATIONS,
      projectId,
      (response) => {
        const quota = response?.data?.quota;
        if (quota) {
          if (quota.tokens?.remaining !== null && quota.tokens?.remaining <= 0) {
            notifyError(t("presentation_tool.quota_exceeded_tokens"));
            return;
          }
          if (quota.file_uploads?.remaining !== null && quota.file_uploads?.remaining <= 0) {
            notifyError(t("presentation_tool.quota_exceeded_files"));
            return;
          }
        }
        startTranslation(file, selectedLanguages);
      },
      () => {
        startTranslation(file, selectedLanguages);
      }
    );
  }, [file, selectedLanguages, startTranslation, getProductQuota, notifyError, t]);

  const handleReset = useCallback(() => {
    reset();
    setFile(null);
    setSelectedLanguages([]);
  }, [reset]);

  const handleDownload = useCallback((url) => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  // Still loading auth or subscription — don't flash the upload UI
  if (authHasCompleted === null || platformPresentations === null) {
    return <PageWrapper />;
  }

  // No presentations subscription — show paywall
  if (!platformPresentations?.enabled) {
    return (
      <PageWrapper>
        <Content>
          <NoAccessCard>
            <NoAccessIcon>
              <span style={{ color: "rgba(112,105,253,0.5)" }}><PresentationIcon /></span>
            </NoAccessIcon>
            <NoAccessTitle>{t("presentation_tool.title")}</NoAccessTitle>
            <NoAccessMessage>{t("presentation_tool.no_access_message")}</NoAccessMessage>
            <NoAccessLink onClick={async () => {
              const currentToken = LocalStorage.getAuthenticationToken();
              if (!currentToken) { window.open(PLATFORM_URL); return; }
              const { getAuthBridgeExchangeToken } = await import("../API/authBridge");
              const exchangeToken = await getAuthBridgeExchangeToken(currentToken);
              if (exchangeToken) {
                if (window.electron?.openExternalUrl) {
                  window.electron.openExternalUrl(`${PLATFORM_URL}/login?bridge=${exchangeToken}`);
                } else {
                  window.open(`${PLATFORM_URL}/login?bridge=${exchangeToken}`);
                }
              } else {
                window.open(PLATFORM_URL);
              }
            }}>
              {t("presentation_tool.go_to_platform")}
            </NoAccessLink>
          </NoAccessCard>
        </Content>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Content>
        <TitleArea>
          <TitleRow>
            <TitleLeft>
              <span style={{ color: "#7069FD" }}><PresentationIcon /></span>
              <Title>{t("presentation_tool.title")}</Title>
            </TitleLeft>
            {isMultiple && (
              <ProjectSelector projects={projects} value={projectId} onChange={setSelectedId} />
            )}
          </TitleRow>
          <Subtitle>{t("presentation_tool.subtitle")}</Subtitle>
        </TitleArea>

        {!hasJobs && (
          <SetupSection>
            <SetupGrid>
              <SetupColumn>
                <DropArea {...getRootProps()} $active={isDragActive} $hasFile={!!file}>
                  <input {...getInputProps()} />
                  {file ? (
                    <FilePreview>
                      <DescriptionOutlinedIcon style={{ fontSize: 28, color: "#7069FD" }} />
                      <FileInfo>
                        <FileName>{file.name}</FileName>
                        <FileSize>{(file.size / 1024 / 1024).toFixed(1)} MB</FileSize>
                      </FileInfo>
                    </FilePreview>
                  ) : (
                    <DropContent>
                      <CloudUploadOutlinedIcon
                        style={{ fontSize: 28, color: isDragActive ? "#7069FD" : "rgba(135,140,145,1)" }}
                      />
                      <DropLabel>
                        {isDragActive
                          ? t("presentation_tool.drop_file_active")
                          : t("presentation_tool.drop_file")}
                      </DropLabel>
                      <DropHint>{t("presentation_tool.drop_hint")}</DropHint>
                    </DropContent>
                  )}
                </DropArea>
              </SetupColumn>

              <SetupColumn>
                <LangHeader>
                  <Label>{t("presentation_tool.languages_label")}</Label>
                  <LangCount>
                    {t("presentation_tool.languages_count", { count: selectedLanguages.length })}
                  </LangCount>
                </LangHeader>

                {selectedLanguages.length > 0 && (
                  <ChipList>
                    {selectedLanguages.map((lang) => (
                      <Chip key={lang}>
                        <ChipLabel>{lang}</ChipLabel>
                        <ChipRemove onClick={() => removeLanguage(lang)}>
                          <CloseIcon style={{ fontSize: 14 }} />
                        </ChipRemove>
                      </Chip>
                    ))}
                  </ChipList>
                )}

                {selectedLanguages.length < MAX_LANGUAGES && (
                  <LanguageBtn
                    value=""
                    setValue={addLanguage}
                    placeholder={t("presentation_tool.add_language")}
                    valuesList={availableLanguages}
                  />
                )}
              </SetupColumn>
            </SetupGrid>

            <PrimaryBtn
              onClick={handleTranslate}
              disabled={!file || selectedLanguages.length === 0}
            >
              {t("presentation_tool.translate")}
            </PrimaryBtn>
          </SetupSection>
        )}

        {hasJobs && (
          <QueueSection>
            <QueueHeader>
              <ProcessingInfo>
                <DescriptionOutlinedIcon style={{ fontSize: 18, color: "#7069FD" }} />
                <ProcessingFile>{file?.name}</ProcessingFile>
              </ProcessingInfo>
              {/* Download all hidden for now — revisit with fetch+blob or server-side zip */}
            </QueueHeader>

            <QueueList>
              {jobs.map((job) => (
                <QueueItem key={job.language} $isActive={job.status === "processing" || job.status === "uploading"}>
                  <QueueRow>
                    <QueueLang>{job.language}</QueueLang>
                    <QueueStatus>
                      {job.status === "completed" && (
                        <CompletedBadge>
                          <CheckCircleOutlineIcon style={{ fontSize: 15, color: "#11B973" }} />
                          <span>{t("presentation_tool.complete")}</span>
                        </CompletedBadge>
                      )}
                      {job.status === "failed" && (
                        <FailedBadge>
                          <ErrorOutlineIcon style={{ fontSize: 15, color: "rgba(238,80,80,1)" }} />
                          <span>{job.error || t("presentation_tool.error_default")}</span>
                        </FailedBadge>
                      )}
                      {job.status === "pending" && (
                        <WaitingText>{t("presentation_tool.waiting")}</WaitingText>
                      )}
                      {(job.status === "uploading" || job.status === "processing") && (
                        <ActiveText>{getJobPhaseText(job, t)}</ActiveText>
                      )}
                    </QueueStatus>
                    {job.status === "completed" && job.downloadUrl && (
                      <DownloadIconBtn onClick={() => handleDownload(job.downloadUrl)}>
                        <FileDownloadOutlinedIcon style={{ fontSize: 18 }} />
                      </DownloadIconBtn>
                    )}
                  </QueueRow>

                  {(job.status === "uploading" || job.status === "processing") && (
                    <ProgressTrack>
                      <ProgressFill style={{ width: `${getJobPercent(job)}%` }} />
                    </ProgressTrack>
                  )}
                </QueueItem>
              ))}
            </QueueList>

            {allDone && (
              <QueueFooter>
                <SuccessMessage>
                  <CheckCircleOutlineIcon style={{ fontSize: 18, color: "#11B973" }} />
                  {t("presentation_tool.queue_complete")}
                </SuccessMessage>
                <GhostBtn onClick={handleReset}>
                  {t("presentation_tool.translate_another")}
                </GhostBtn>
              </QueueFooter>
            )}
          </QueueSection>
        )}
      </Content>
    </PageWrapper>
  );
};

export default PresentationTranslationToolPage;

/* ── Animations ── */

const fillProgress = keyframes`
  from { background-position: 200% 0; }
  to   { background-position: -200% 0; }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ── Layout ── */

const PageWrapper = styled.div`
  min-height: 100%;
  width: 100%;
  padding: 48px 24px 64px;
  background: ${({ theme }) => theme.menu.background};
  box-sizing: border-box;
  overflow-y: auto;
`;

const Content = styled.div`
  max-width: 720px;
  margin: 0 auto;
  animation: ${fadeUp} 0.35s cubic-bezier(0.16, 1, 0.3, 1);
`;

/* ── No access ── */

const NoAccessCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 64px 32px;
  text-align: center;
`;

const NoAccessIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: rgba(112, 105, 253, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
`;

const NoAccessTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  margin: 0;
`;

const NoAccessMessage = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  margin: 0;
  max-width: 360px;
  line-height: 1.6;
`;

const NoAccessLink = styled.a`
  margin-top: 8px;
  font-size: 14px;
  font-weight: 600;
  color: rgba(112, 105, 253, 1);
  text-decoration: none;
  cursor:pointer;

  &:hover {
    text-decoration: underline;
  }
`;

/* ── Title ── */

const TitleArea = styled.div`
  margin-bottom: 32px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const TitleLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  letter-spacing: -0.3px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  font-weight: 400;
  margin: 0;
  color: ${({ theme }) => theme.menu.infoText};
  padding-left: 32px;
`;

/* ── Setup ── */

const SetupSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SetupGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const SetupColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

/* ── Drop zone ── */

const DropArea = styled.div`
  border: 1.5px dashed
    ${({ $active, $hasFile }) =>
      $active ? "#7069FD" : $hasFile ? "rgba(112,105,253,0.35)" : "rgba(215,214,238,1)"};
  border-radius: 10px;
  padding: ${({ $hasFile }) => ($hasFile ? "16px 18px" : "32px 18px")};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s, background 0.2s;
  background: ${({ $active }) =>
    $active ? "rgba(112,105,253,0.03)" : "transparent"};
  min-height: 120px;

  &:hover {
    border-color: rgba(112, 105, 253, 0.5);
    background: rgba(112, 105, 253, 0.02);
  }
`;

const DropContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const DropLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  text-align: center;
`;

const DropHint = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.menu.infoText};
`;

const FilePreview = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const FileInfo = styled.div`
  min-width: 0;
  flex: 1;
`;

const FileName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileSize = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.menu.infoText};
  margin-top: 1px;
`;

/* ── Language selector ── */

const LangHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;

const LangCount = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.menu.infoText};
`;

const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 8px 5px 10px;
  border-radius: 6px;
  background: rgba(112, 105, 253, 0.08);
  border: 1px solid rgba(112, 105, 253, 0.15);
`;

const ChipLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;

const ChipRemove = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${({ theme }) => theme.menu.infoText};
  border-radius: 3px;
  transition: color 0.15s;

  &:hover {
    color: rgba(238, 80, 80, 1);
  }
`;

/* ── Buttons ── */

const PrimaryBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  border: none;
  border-radius: 9px;
  color: #fff;
  background: #7069fd;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.1s;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const GhostBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 9px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  background: transparent;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: rgba(112, 105, 253, 0.04);
    border-color: rgba(112, 105, 253, 0.3);
  }
`;

const DownloadAllBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  border: none;
  border-radius: 7px;
  color: #fff;
  background: #7069fd;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.9;
  }
`;

const DownloadIconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  background: none;
  cursor: pointer;
  color: #7069fd;
  border-radius: 5px;
  transition: background 0.15s;
  flex-shrink: 0;

  &:hover {
    background: rgba(112, 105, 253, 0.08);
  }
`;

/* ── Queue ── */

const QueueSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: ${fadeUp} 0.3s cubic-bezier(0.16, 1, 0.3, 1);
`;

const QueueHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ProcessingInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const ProcessingFile = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const QueueList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const QueueItem = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  background: ${({ $isActive }) =>
    $isActive ? "rgba(112,105,253,0.04)" : "transparent"};
  border: 1px solid
    ${({ $isActive, theme }) =>
      $isActive ? "rgba(112,105,253,0.12)" : theme.sidebar.borderColor};
  transition: background 0.2s, border-color 0.2s;
`;

const QueueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const QueueLang = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  min-width: 100px;
`;

const QueueStatus = styled.div`
  flex: 1;
  min-width: 0;
`;

const WaitingText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.menu.infoText};
`;

const ActiveText = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #7069fd;
`;

const CompletedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #11b973;
`;

const FailedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: rgba(238, 80, 80, 1);
`;

/* ── Progress bar ── */

const ProgressTrack = styled.div`
  height: 4px;
  border-radius: 2px;
  background: rgba(112, 105, 253, 0.1);
  overflow: hidden;
  margin-top: 10px;
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: 2px;
  background: linear-gradient(90deg, #7069fd 0%, #928bf7 50%, #7069fd 100%);
  background-size: 200% 100%;
  animation: ${fillProgress} 2s linear infinite;
  transition: width 0.6s cubic-bezier(0.16, 1, 0.3, 1);
`;

/* ── Queue footer ── */

const QueueFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding-top: 8px;
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;
